import { config } from './config';
import { getIdToken } from './auth';

/** The envelope every endpoint returns. */
interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Field-keyed messages from a 400, ready to hang off form inputs. */
    readonly fieldErrors?: Record<string, string>,
    readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Public endpoints (storefront, agreement signing) send no token. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

function isFieldErrors(details: unknown): details is Record<string, string> {
  return (
    typeof details === 'object' &&
    details !== null &&
    !Array.isArray(details) &&
    Object.values(details).every((v) => typeof v === 'string')
  );
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, signal } = options;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (!anonymous) {
    const token = await getIdToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      method,
      headers,
      signal,
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('Cannot reach the server. Check your connection and try again.', 0);
  }

  if (response.status === 204) return undefined as T;

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`The server returned an unexpected response (${response.status}).`, response.status);
  }

  if (!response.ok || !envelope.success) {
    const message = envelope.error?.message ?? `Request failed (${response.status})`;
    const details = envelope.error?.details;
    throw new ApiError(
      message,
      response.status,
      isFieldErrors(details) ? details : undefined,
      details
    );
  }

  return envelope.data as T;
}

/** Upload a file straight to S3 using a presigned URL from the API. */
export async function uploadToPresignedUrl(
  url: string,
  file: File,
  contentType: string
): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    // These must match the headers the URL was signed with, or S3 rejects it.
    headers: { 'Content-Type': contentType, 'x-amz-server-side-encryption': 'AES256' },
    body: file,
  });
  if (!response.ok) {
    throw new ApiError(`The upload did not complete (${response.status}).`, response.status);
  }
}
